import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'use-analytics';
import { QueryKey } from '@/Config/api/queryKey';
import type { LoginResponse } from '@/Domain/api/User/dto/User.dto';
import { completeLogin } from '@/Domain/hooks/Auth/completeLogin';
import { isPasskeyCeremonyCancelled } from '@/Domain/utils/passkeyErrors';

const TOO_MANY_REQUESTS_STATUS = 429;
const PASSKEY_NOT_FOUND = 'PASSKEY_NOT_FOUND';

type ApiErrorBody = { errors?: Record<string, { message: string; code: string }[]> };

export const usePasskeyLogin = () => {
  const { t } = useTranslation(['login', 'ERRORS']);
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSupported] = useState(() => browserSupportsWebAuthn());

  const mutation = useMutation<LoginResponse, unknown, void>({
    mutationKey: [QueryKey.accounts.passkeyLogin],
    throwOnError: false,
    onSuccess: (response) => completeLogin(response, postHog, 'passkey'),
    onError: (error) => {
      if (isPasskeyCeremonyCancelled(error)) {
        return;
      }
      track('login_passkey_error', { event_category: 'User login', event_label: 'login_passkey' });

      const axiosError = error as AxiosError<ApiErrorBody>;
      const code = axiosError.response?.data?.errors?.root?.[0]?.code;
      if (code === PASSKEY_NOT_FOUND) {
        setErrorMessage(t('login:passkey.error_not_found'));
        return;
      }
      if (axiosError.response?.status === TOO_MANY_REQUESTS_STATUS) {
        setErrorMessage(t('ERRORS:TOO_MANY_REQUESTS'));
        return;
      }
      setErrorMessage(t('login:passkey.error_generic'));
    },
  });

  const signInWithPasskey = () => {
    setErrorMessage(null);
    track('login_passkey_submit', { event_category: 'User login', event_label: 'login_passkey' });
    mutation.mutate();
  };

  return {
    isSupported,
    signInWithPasskey,
    // Stays pending through the redirect so the button can't be clicked twice.
    isPending: mutation.isPending || mutation.isSuccess,
    errorMessage,
  };
};
